import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  createOrUpdatePublicQuoteDraft,
  type PublicQuoteAddress,
  type PublicQuotePartnerSummary,
  type PublicQuotePayload,
} from '../lib/quote/api';

type StoredAddress = {
  line_1: string;
  line_2: string;
  post_town: string;
  county: string;
  postcode: string;
  latitude?: string | number;
  longitude?: string | number;
  formatted_address?: string;
  formattedAddress?: string;
  placeId?: string;
  addressLine1?: string;
  addressLine2?: string;
  townCity?: string;
};

type StoredPartnerDetails = any;

function normalizeAddress(address?: PublicQuoteAddress): StoredAddress | null {
  if (!address || !Object.values(address).some(value => value != null && String(value).trim() !== '')) return null;

  const addressLine1 = address.addressLine1 || '';
  const addressLine2 = address.addressLine2 || '';
  const townCity = address.townCity || '';
  const county = address.county || '';
  const latitude = address.latitude == null ? undefined : String(address.latitude);
  const longitude = address.longitude == null ? undefined : String(address.longitude);
  const formattedAddress = address.formattedAddress || '';

  return {
    line_1: addressLine1,
    line_2: addressLine2,
    post_town: townCity,
    county,
    postcode: '',
    latitude,
    longitude,
    formatted_address: formattedAddress,
    formattedAddress,
    placeId: address.placeId || '',
    addressLine1,
    addressLine2,
    townCity,
  };
}

function normalizePartnerDetails(
  quote: PublicQuotePayload,
  partnerDetails?: PublicQuotePartnerSummary | null,
): StoredPartnerDetails {
  const partner = partnerDetails || quote.partnerDetails;
  const partnerId = partner?.partnerId;
  const companyName = partner?.companyName || quote.companyName || '';
  const companyType = partner?.companyType || quote.companyType || '';
  const pricingStyle = partner?.pricingStyle || quote.pricingStyle || '';
  const calloutLabour = partner?.calloutLabour ?? quote.calloutLabour ?? null;
  const calloutOnly = partner?.calloutOnly ?? quote.calloutOnly ?? null;
  const hourlyRate = partner?.hourlyRate ?? quote.hourlyRate ?? null;
  const commission = partner?.commission ?? quote.commission ?? 0;
  const latency = partner?.latency || quote.latency || '';
  const phoneNumber = partner?.phoneNumber || '';
  const email = partner?.email || '';

  if (!partnerId && !companyName && !phoneNumber && !email) {
    return null;
  }

  return {
    ...(partner || {}),
    partner: partnerId || '',
    partnerId: partnerId || '',
    userid: partnerId || '',
    company_name: companyName,
    companyName,
    company_type: companyType,
    companyType,
    pricing_style: pricingStyle,
    pricingStyle,
    callout_labour: calloutLabour,
    calloutLabour,
    callout_only: calloutOnly,
    calloutOnly,
    hourly_rate: hourlyRate,
    hourlyRate,
    commission,
    latency,
    phone_number: phoneNumber,
    phoneNumber,
    email,
    service_name: quote.serviceName || '',
    serviceName: quote.serviceName || '',
  };
}

function getCustomerName(quote: PublicQuotePayload) {
  if (quote.customerName) return quote.customerName;
  return [quote.firstName || '', quote.lastName || ''].filter(Boolean).join(' ').trim();
}

function isPlaceholderUnknownValue(value?: string | null) {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'unknown / not listed' || normalized === 'unknown/not listed' || normalized === 'unknown';
}

interface QuoteState {
  quoteId: string | null;
  accessToken: string | null;
  quoteStatus: string | null;
  jobStatus: string | null;
  appointmentDate: string | null;
  applianceType: string;
  applianceName: string;
  brand: string;
  fault: string;
  customFault: string;
  postcode: string;
  address: any;
  postcodeId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  partnerDetails: any;
  gclid: string;
  msclkid: string;
  fbclid: string;
  customerRef: string;
  isEmbed: boolean;
  currentStep: string;
  serviceCategory: string;
  setQuoteId: (id: string) => void;
  setAccessToken: (token: string | null) => void;
  setServiceCategory: (category: string) => void;
  createQuote: (faultDescription?: string) => Promise<string>;
  applyPublicQuote: (quote: PublicQuotePayload, accessToken?: string | null) => void;
  setAppliance: (type: string, name: string) => void;
  setBrand: (brand: string) => void;
  setFault: (fault: string, isCustom?: boolean) => void;
  setAddress: (postcode: string, address: any, postcodeId: string) => void;
  setCustomerDetails: (name: string, phone: string, email: string) => void;
  setPartnerDetails: (details: any) => void;
  setTrackingParams: (params: { gclid?: string; msclkid?: string; fbclid?: string; customerRef?: string }) => void;
  setIsEmbed: (isEmbed: boolean) => void;
  setStep: (step: string) => void;
  resetForm: () => void;
}

export const useQuoteStore = create<QuoteState>()(
  persist(
    (set, get) => ({
      quoteId: null,
      accessToken: null,
      quoteStatus: null,
      jobStatus: null,
      appointmentDate: null,
      applianceType: '',
      applianceName: '',
      brand: '',
      fault: '',
      customFault: '',
      postcode: '',
      address: null,
      postcodeId: '',
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      partnerDetails: null,
      gclid: 'unavailable',
      msclkid: 'unavailable',
      fbclid: 'unavailable',
      customerRef: '',
      isEmbed: false,
      currentStep: 'step1',
      serviceCategory: '',

      setQuoteId: (id) => set({ quoteId: id }),
      setAccessToken: (token) => set({ accessToken: token }),
      setServiceCategory: (category) => set({ serviceCategory: category }),

      createQuote: async (faultDescription) => {
        set({ brand: 'Unknown' });
        const state = get();
        const submittedFault = faultDescription ?? (state.customFault || state.fault || undefined);
        const isDraftStatus = (status: string | null) => !status || /^(new quote|quote draft|draft)$/i.test(status.trim());
        const canReuseDraft = Boolean(
          state.quoteId && state.accessToken && !state.appointmentDate
          && (state.quoteStatus || state.jobStatus)
          && isDraftStatus(state.quoteStatus) && isDraftStatus(state.jobStatus),
        );
        const response = await createOrUpdatePublicQuoteDraft({
          quoteId: canReuseDraft ? state.quoteId! : undefined,
          accessToken: canReuseDraft ? state.accessToken! : undefined,
          serviceId: state.applianceType || '',
          serviceName: state.applianceName || '',
          applianceBrand: 'Unknown',
          faultDescription: submittedFault,
          gclid: state.gclid || '',
          msclkid: state.msclkid || '',
          fbclid: state.fbclid || '',
          customerRef: state.customerRef || '',
          source: 'Web',
        });

        get().applyPublicQuote({
          ...response.quote,
          serviceId: response.quote.serviceId || state.applianceType,
          serviceName: response.quote.serviceName || state.applianceName,
          applianceBrand: state.brand || response.quote.applianceBrand,
          faultDescription: submittedFault || response.quote.faultDescription,
        }, response.accessToken);
        set({ customFault: state.customFault && submittedFault === state.customFault ? state.customFault : '' });
        return response.quoteId;
      },

      applyPublicQuote: (quote, accessToken) => set((state) => {
        const isSameQuote = quote.quoteId === state.quoteId;
        const incomingAddress = normalizeAddress(quote.address);
        const previousAddress = isSameQuote ? state.address : null;
        const address = incomingAddress
          ? {
              ...previousAddress,
              ...Object.fromEntries(Object.entries(incomingAddress).filter(([, value]) => value != null && String(value).trim() !== '')),
            } as StoredAddress
          : previousAddress ? { ...previousAddress } : null;
        if (address) {
          address.postcode = quote.postcode || (isSameQuote ? state.postcode : '') || '';
        }

        const quoteBrand = quote.applianceBrand || '';
        const quoteFault = quote.faultDescription || '';
        const shouldKeepExistingBrand = isPlaceholderUnknownValue(quoteBrand);
        const shouldKeepExistingFault = isPlaceholderUnknownValue(quoteFault);

        return {
          quoteId: quote.quoteId,
          accessToken: accessToken === undefined ? (isSameQuote ? state.accessToken : null) : accessToken,
          quoteStatus: quote.status ?? (isSameQuote ? state.quoteStatus : null),
          jobStatus: quote.jobStatus ?? (isSameQuote ? state.jobStatus : null),
          appointmentDate: quote.appointmentDate === undefined ? (isSameQuote ? state.appointmentDate : null) : quote.appointmentDate,
          applianceType: quote.serviceId || (isSameQuote ? state.applianceType : ''),
          applianceName: quote.serviceName || (isSameQuote ? state.applianceName : ''),
          brand: shouldKeepExistingBrand && isSameQuote ? state.brand : (quoteBrand || (isSameQuote ? state.brand : '')),
          fault: shouldKeepExistingFault && isSameQuote ? state.fault : (quoteFault || (isSameQuote ? state.fault : '')),
          customFault: isSameQuote ? state.customFault : '',
          postcode: quote.postcode || (isSameQuote ? state.postcode : ''),
          address,
          postcodeId: quote.postcodeId || (isSameQuote ? state.postcodeId : ''),
          customerName: getCustomerName(quote) || (isSameQuote ? state.customerName : ''),
          customerPhone: quote.customerPhone || (isSameQuote ? state.customerPhone : ''),
          customerEmail: quote.customerEmail || (isSameQuote ? state.customerEmail : ''),
          partnerDetails: normalizePartnerDetails(quote) ?? (isSameQuote ? state.partnerDetails : null),
        };
      }),

      setAppliance: (type, name) => set((state) => ({
        applianceType: type,
        applianceName: name,
        ...(type !== state.applianceType ? {
          quoteId: null,
          accessToken: null,
          quoteStatus: null,
          jobStatus: null,
          appointmentDate: null,
          postcode: '',
          address: null,
          postcodeId: '',
          partnerDetails: null,
          brand: '',
          fault: '',
          customFault: '',
        } : {}),
      })),
      setBrand: (brand) => set({ brand }),
      setFault: (fault, isCustom = false) => set(isCustom ? { customFault: fault } : { fault, customFault: '' }),
      setAddress: (postcode, address, postcodeId) => set({ postcode, address, postcodeId }),
      setCustomerDetails: (name, phone, email) => set({ customerName: name, customerPhone: phone, customerEmail: email }),
      setPartnerDetails: (details) => set({ partnerDetails: details }),
      setTrackingParams: (params) => set((state) => ({ ...state, ...params })),
      setIsEmbed: (isEmbed) => set({ isEmbed }),
      setStep: (step) => set({ currentStep: step }),

      resetForm: () => set((state) => ({
        quoteId: null,
        accessToken: null,
        quoteStatus: null,
        jobStatus: null,
        appointmentDate: null,
        applianceType: state.isEmbed ? state.applianceType : '',
        applianceName: state.isEmbed ? state.applianceName : '',
        brand: '',
        fault: '',
        customFault: '',
        postcode: '',
        address: null,
        postcodeId: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        partnerDetails: null,
        isEmbed: state.isEmbed,
        serviceCategory: state.isEmbed ? state.serviceCategory : '',
        currentStep: 'step1',
      })),
    }),
    {
      name: 'fast-repair-quote-storage',
    },
  ),
);
